import React, { createContext, useContext, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/*
  Region Context
  Manages region detection for A/B testing (e.g., DACH vs GCC).
  Defaults to 'DACH' (Europe) but can be overridden via URL query param ?region=gcc
*/

export type Region = 'DACH' | 'GCC' | 'NA';

interface RegionContextType {
    region: Region;
    setRegion: (region: Region) => void;
}

const RegionContext = createContext<RegionContextType | undefined>(undefined);

export const RegionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [region, setRegion] = useState<Region>('DACH');
    const location = useLocation();

    useEffect(() => {
        // Check for URL override
        const params = new URLSearchParams(location.search);
        const regionParam = params.get('region');

        if (regionParam) {
            if (regionParam.toLowerCase() === 'gcc') {
                setRegion('GCC');
            } else if (regionParam.toLowerCase() === 'dach') {
                setRegion('DACH');
            }
        }
    }, [location.search]);

    return (
        <RegionContext.Provider value={{ region, setRegion }}>
            {children}
        </RegionContext.Provider>
    );
};

export const useRegion = () => {
    const context = useContext(RegionContext);
    if (context === undefined) {
        throw new Error('useRegion must be used within a RegionProvider');
    }
    return context;
};
